import { PanelModel } from '@syncfusion/ej2-layouts/src/dashboard-layout/dashboard-layout-model';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomField } from 'common/models/Configuration/CustomFields';
import { DashboardLayoutComponent } from '@syncfusion/ej2-react-layouts/src/dashboard-layout/dashboardlayout.component';
import { useTheme } from '@mui/material/styles';
import { GetCustomFieldsByEntity_OnlyActive } from 'services/CustomFieldsService';
import { CustomFieldSettingsMap, CustomFieldType } from 'common/models/ScreenLayouts/CustomFields';
import {
  GetCustomerSettingBySettingName,
  UpdateCustomerSettingBySettingName,
} from 'services/ConfigurationService';
import {
  PlacementRecordDashboardElementDefinition,
  PlacementRecordDashboardElementType,
} from 'common/models/Dashboard/EditLayout';
import moment from 'moment';
import { PlaceholderPlacement } from 'util/Definitions/Placement';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import { UdfPlacementFieldMapObj } from 'util/Definitions/ScreenLayouts/CustomFields';
import PanelWrapper from 'components/Dashboards/PanelWrapper';
import useUnsavedChangesDialog from 'hooks/UseUnsavedChangesDialog';
import ConfirmationDialog from 'components/Dialogs/Generic/ConfirmationDialog';
import AddPlacementDashboardElementDialog from 'components/Dialogs/Dashboard/AddPlacementDashboardElementDialog';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  DefaultPlacementQuickViewLayoutElements_Contract,
  DefaultPlacementQuickViewLayoutElements_FixedTermContract,
  DefaultPlacementQuickViewLayoutElements_MarginOnlyContract,
  DefaultPlacementQuickViewLayoutElements_MaxTermContract,
  DefaultPlacementQuickViewLayoutElements_Permanent,
  GetElementTypeById,
  GetMinSizeByElementType,
  GetPanelDefinitionsFromPanelModels,
  PlacementQuickViewLayoutSettings,
} from 'util/Definitions/QuickViewLayout/Placement';
import SingleFieldElement from 'components/ScreenLayouts/SingleFieldElement';
import ViewClientLocationElement from 'components/ScreenLayouts/Clients/ViewClientLocationElement';
import MultiLineTextElement from 'components/ScreenLayouts/MultiLineTextElement';

interface Props {
  placementType: 'Permanent' | 'Contract' | 'FixedTermContract' | 'MaxTermContract' | 'MarginOnly';
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
}

const placement = PlaceholderPlacement;

const LayoutElementsToString = (elements: PanelModel[]) => {
  if (elements.length === 0) return '';
  const s = elements.map((e) => ElementString(e));
  s.sort();
  return s.join('|');
};

const ElementString = (element: PanelModel) => {
  const type = GetElementTypeById(element.id ?? '');
  const val = `${type}|${element.row}|${element.col}|${element.sizeX}|${element.sizeY}`;
  return val;
};

const { unitWidth, unitHeight, gapX, gapY, columns } = PlacementQuickViewLayoutSettings;
const cellSpacing = [gapX, gapY];

export default function QuickViewLayoutAdmin({
  placementType,
  loadingHandler,
  errorHandler,
}: Props) {
  const [layoutSwapControl, setLayoutSwapControl] = useState(true);
  const [screenResizedControl, setScreenResizedControl] = useState(false);
  const [showAddElementDialog, setShowAddElementDialog] = useState(false);
  const [fetchingSavedState, setFetchingSavedState] = useState(false);
  const [fetchingActiveFields, setFetchingActiveFields] = useState(false);
  const [fetchedSavedState, setFetchedSavedState] = useState(false);
  const [activeFields, setActiveFields] = useState<CustomField[]>([]);
  const [initialState, setInitialState] = useState<PanelModel[] | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetToDefaultConfirmDialog, setShowResetToDefaultConfirmDialog] = useState(false);
  const [lastSavedState, setLastSavedState] = useState<PanelModel[] | null>(null);
  const layoutRef = useRef<DashboardLayoutComponent | null>(null);
  const [netRevenueLabel, setNetRevenueLabel] = useState('Net Revenue');
  const theme = useTheme();

  const layoutDataSettingName = useMemo(() => {
    return `PlacementQuickViewLayoutElements_${placementType}`;
  }, [placementType]);

  const defaultElements = useMemo(() => {
    switch (placementType) {
      case 'Permanent':
        return DefaultPlacementQuickViewLayoutElements_Permanent;
      case 'Contract':
        return DefaultPlacementQuickViewLayoutElements_Contract;
      case 'FixedTermContract':
        return DefaultPlacementQuickViewLayoutElements_FixedTermContract;
      case 'MaxTermContract':
        return DefaultPlacementQuickViewLayoutElements_MaxTermContract;
      case 'MarginOnly':
        return DefaultPlacementQuickViewLayoutElements_MarginOnlyContract;
    }
  }, [placementType]);

  useEffect(() => {
    const windowResizeHandler = () => setScreenResizedControl((prev) => !prev);
    window.addEventListener('resize', windowResizeHandler);
    return () => window.removeEventListener('resize', windowResizeHandler);
  }, []);

  useEffect(() => {
    const getNetRevenueLabel = async () => {
      const res = await GetCustomerSettingBySettingName('NetRevenueName');
      if (res) setNetRevenueLabel(res);
    };
    getNetRevenueLabel();
  }, []);

  useEffect(() => {
    const getActiveFields = async () => {
      setFetchingActiveFields(true);
      const res = await GetCustomFieldsByEntity_OnlyActive(5);
      if (res) setActiveFields(res);
      setFetchingActiveFields(false);
    };
    getActiveFields();
  }, []);

  const customFieldsSettingsMap = useMemo<CustomFieldSettingsMap>(() => {
    const obj: CustomFieldSettingsMap = {};
    activeFields.forEach((u) => {
      const customFieldNumber = u.name.substring(13);
      const key = ('CustomField' + customFieldNumber) as CustomFieldType;
      obj[key] = {
        title: u.agencyName,
        isMultiLine: u.multiLine && !u.usePredefinedValues,
        isEditable: u.editable,
      };
    });
    return obj;
  }, [activeFields]);

  useEffect(() => {
    const getSavedState = async () => {
      setFetchingSavedState(true);
      const elementsJson = await GetCustomerSettingBySettingName(layoutDataSettingName);
      if (elementsJson) {
        const panels = JSON.parse(elementsJson) as PanelModel[];
        setLastSavedState(panels);
        setInitialState(panels);
      }
      setFetchingSavedState(false);
      setFetchedSavedState(true);
    };
    getSavedState();
  }, [layoutDataSettingName]);

  useEffect(() => {
    loadingHandler && loadingHandler(fetchingSavedState || fetchingActiveFields);
  }, [loadingHandler, fetchingSavedState, fetchingActiveFields]);

  const initialStateString = useMemo(() => {
    if (initialState && initialState.length) return LayoutElementsToString(initialState);
    return '';
  }, [initialState]);

  const layoutChangeHandler = useCallback(() => {
    const api = layoutRef.current;
    if (api) {
      const currentData = api.serialize();
      const currentStringData = LayoutElementsToString(currentData);
      setHasChanges(currentStringData !== initialStateString);
    }
  }, [initialStateString]);

  useEffect(() => {
    layoutChangeHandler();
  }, [layoutSwapControl, layoutChangeHandler]);

  const elements = useMemo<PlacementRecordDashboardElementDefinition[]>(() => {
    if (!fetchedSavedState) return [];
    if (lastSavedState !== null) {
      const elements = GetPanelDefinitionsFromPanelModels(lastSavedState);
      return elements;
    }
    return defaultElements;
  }, [lastSavedState, fetchedSavedState, defaultElements]);

  const removeElementHandler = useCallback(
    (id: string) => {
      const api = layoutRef.current;
      if (api) {
        api.removePanel(id);
        layoutChangeHandler();
      }
    },
    [layoutChangeHandler]
  );

  const addElementHandler = useCallback(
    (newElementType: PlacementRecordDashboardElementType) => {
      const api = layoutRef.current;
      if (api) {
        const m = moment().format('YYYYMMDDhhmmssSS');
        const id = `${newElementType}_${m}`;
        const canGrowY =
          newElementType === 'PlacementSummary' || newElementType.startsWith('CustomField');
        const [minX, minY] = GetMinSizeByElementType(newElementType);
        const maxSizeY = canGrowY ? undefined : minY;
        api.addPanel({ id: id, col: 0, row: 0, sizeX: 1, sizeY: 1 });
        api.updatePanel({
          id: id,
          col: 0,
          row: 0,
          sizeX: minX,
          sizeY: minY,
          minSizeX: minX,
          minSizeY: minY,
          maxSizeX: 1,
          maxSizeY: maxSizeY,
          enabled: false,
        });
        const newDataObj = api.serialize();
        setLastSavedState(newDataObj);
        setLayoutSwapControl((prev) => !prev);
        setShowAddElementDialog(false);
        layoutChangeHandler();
      }
    },
    [layoutChangeHandler]
  );

  const renderElement = useCallback(
    (id: string, type: PlacementRecordDashboardElementType) => {
      if (placement) {
        switch (type) {
          case 'Divider':
            return (
              <Box pt="20px">
                <Divider component="div" />
              </Box>
            );
          case 'Spacer':
            return <></>;
          case 'PlacementId':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="ID"
                fieldValue={placement.id.toString()}
              />
            );
          case 'PlacementJobId':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Job ID" format="custom">
                <Box component="span" color="primary.main">
                  {placement.jobID}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementType':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Job Type"
                fieldValue={placement.placementTypeName}
              />
            );
          case 'PlacementJobTitle':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Job Title"
                fieldValue={placement.jobTitle}
              />
            );
          case 'PlacementCandidate':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Candidate" format="custom">
                <Box component="span" color="primary.main">
                  {placement.candidateName}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementStartDate':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Start Date"
                fieldValue={placement.startDate}
                format="date"
              />
            );
          case 'PlacementEndDate':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="End Date"
                fieldValue={placement.endDate}
                format="date"
              />
            );
          case 'PlacementDivision':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Division" format="custom">
                <Chip size="small" label="Placeholder Division" sx={{ mr: '5px' }} />
              </SingleFieldElement>
            );
          case 'PlacementPayDivision':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Pay Division" format="custom">
                <Chip size="small" label="Placeholder Pay Division" sx={{ mr: '5px' }} />
              </SingleFieldElement>
            );
          case 'PlacementCurrency':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Currency"
                fieldValue={placement.currencyID.toString()}
              />
            );
          case 'PlacementSalaryPackage':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Salary Package"
                fieldValue="100,000 Yearly"
              />
            );
          case 'PlacementInvoiceFee':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Fee"
                fieldValue="10 Percent (10,000)"
              />
            );
          case 'PlacementRatesCalculator':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="" format="custom">
                <Box component="span" color="primary.main">
                  Rates Calculator
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementChargeRate':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Charge Rate"
                fieldValue="55.00"
              />
            );
          case 'PlacementPayRate':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Pay Rate"
                fieldValue="55.0"
              />
            );
          case 'PlacementOnCost':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="On Costs"
                fieldValue="55.0"
              />
            );
          case 'PlacementMargin':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Margin"
                fieldValue="28.00 (28%)"
              />
            );
          case 'PlacementHoursPerDay':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Hours Per Day"
                fieldValue={placement.hoursPerDay.toString()}
              />
            );
          case 'PlacementDaysPerWeek': {
            const daysPerWeek =
              placement.daysPerWeek >= 1 && placement.daysPerWeek <= 7
                ? placement.daysPerWeek.toString()
                : '';
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Days Per Week"
                fieldValue={`${daysPerWeek} Day${placement.daysPerWeek > 1 ? 's' : ''}`}
              />
            );
          }

          case 'PlacementComments':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Pay Comments"
                fieldValue={placement.payComments}
              />
            );
          case 'PlacementPaymentType':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Payment Type"
                fieldValue="Employee"
              />
            );
          case 'PlacementPayCompany':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Pay Company" format="custom">
                <Box component="span" color="primary.main">
                  {placement.paymentCompanyName}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementPayContact':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Pay Contact" format="custom">
                <Box component="span" color="primary.main">
                  {placement.paymentContactName}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementWarranty':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Warranty"
                fieldValue={placement.warrantyLength + ' Month(s)'}
              />
            );
          case 'PlacementLocation':
            return (
              <ViewClientLocationElement
                disableTextSelection
                address1={placement.address1 ?? ''}
                address2={placement.address2 ?? ''}
                address3={placement.address3 ?? ''}
                countryName={placement.countryName ?? ''}
                postcode={placement.postcode ?? ''}
                siteId={placement.siteID}
                siteName={placement.siteName ?? ''}
                state={placement.state ?? ''}
                suburb={placement.suburb ?? ''}
              />
            );
          case 'PlacementClient':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Client" format="custom">
                <Box component="span" color="primary.main">
                  {placement.clientName}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementContact':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Contact" format="custom">
                <Box component="span" color="primary.main">
                  {placement.contactName}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementHiringManager':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Hiring Mgr" format="custom">
                <Box component="span" color="primary.main">
                  {placement.hiringManagerName}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementBillingGroup':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Billing Group" format="custom">
                <Box component="span" color="primary.main">
                  {placement.billingGroupName}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementBilling1':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Billing 1" format="custom">
                <Box component="span" color="primary.main">
                  {placement.billingContactName}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementBilling2':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Billing 2" format="custom">
                <Box component="span" color="primary.main">
                  {placement.billingContact2Name}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementBilling3':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Billing 3" format="custom">
                <Box component="span" color="primary.main">
                  {placement.billingContact3Name}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementSignatory':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Signatory" format="custom">
                <Box component="span" color="primary.main">
                  {placement.signatoryName}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementClientRef':
            return (
              <SingleFieldElement
                useEllipsisForLongValues
                fieldTitle="Client Ref"
                fieldValue={placement.clientReference}
              />
            );
          case 'PlacementApprover1':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Approver 1" format="custom">
                <Box component="span" color="primary.main">
                  {placement.timesheetApprover1Name}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementApprover2':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Approver 2" format="custom">
                <Box component="span" color="primary.main">
                  {placement.timesheetApprover2Name}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementSite':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Site" format="custom">
                <Box component="span" color="primary.main">
                  {placement.siteName}
                </Box>
              </SingleFieldElement>
            );
          case 'PlacementSummary':
            return (
              <MultiLineTextElement
                disableTextSelection
                title="Summary"
                content={placement.summary ?? ''}
              />
            );
          case 'PlacementStatus':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Status"
                fieldValue={placement.placementStatusName}
              />
            );
          case 'PlacementConsultant1':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Consultant 1"
                fieldValue={
                  placement.consultantID1
                    ? `${placement.consultantName1} (${placement.consultantPercentage1}%)`
                    : ''
                }
              />
            );
          case 'PlacementConsultant2':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Consultant 2"
                fieldValue={
                  placement.consultantID2
                    ? `${placement.consultantName2} (${placement.consultantPercentage2}%)`
                    : ''
                }
              />
            );
          case 'PlacementConsultant3':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Consultant 3"
                fieldValue={
                  placement.consultantID3
                    ? `${placement.consultantName3} (${placement.consultantPercentage3}%)`
                    : ''
                }
              />
            );
          case 'PlacementConsultant4':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Consultant 4"
                fieldValue={
                  placement.consultantID4
                    ? `${placement.consultantName4} (${placement.consultantPercentage4}%)`
                    : ''
                }
              />
            );
          case 'PlacementConsultant5':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Consultant 5"
                fieldValue={
                  placement.consultantID5
                    ? `${placement.consultantName5} (${placement.consultantPercentage5}%)`
                    : ''
                }
              />
            );
          case 'PlacementSource':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Source"
                fieldValue={placement.source}
              />
            );
          case 'PlacementNetRevenue':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle={netRevenueLabel}
                fieldValue="5,000.00"
              />
            );
          case 'PlacementDate':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Placement Date"
                fieldValue={placement.placementDate}
                format="date"
              />
            );
          case 'PlacementProbation':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Probation"
                fieldValue={placement.probationLength + ' Month(s)'}
              />
            );
          case 'PlacementNotice':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Candidate Notice"
                fieldValue="15 Day(s)"
              />
            );
          case 'PlacementClientNotice':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Client Notice"
                fieldValue="15 Day(s)"
              />
            );
          case 'PlacementPayFreq':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Pay Frequency"
                fieldValue="Fortnightly"
              />
            );
          case 'PlacementComplianceChecklist':
            return (
              <SingleFieldElement
                useEllipsisForLongValues
                fieldTitle="Compliance Checklist"
                fieldValue={placement.complianceChecklistName}
              />
            );
          default:
            if (type.startsWith('CustomField')) {
              const settings = customFieldsSettingsMap[type];
              if (settings) {
                const title = settings.title;
                const { field, format } = UdfPlacementFieldMapObj[type as CustomFieldType];
                const udfValue = placement[field];
                if (format === 'string' || format === 'date' || format === 'datetime') {
                  return (
                    <SingleFieldElement
                      disableTextSelection
                      isMultiLine={settings.isMultiLine}
                      format={format}
                      fieldTitle={title}
                      fieldValue={udfValue ? udfValue.toString() : null}
                    />
                  );
                }
                if (format === 'number') {
                  return (
                    <SingleFieldElement
                      disableTextSelection
                      format={format}
                      fieldTitle={title}
                      fieldValue={udfValue ? +udfValue : 0}
                    />
                  );
                }
              } else
                return (
                  <SingleFieldElement
                    disableTextSelection
                    fieldTitle={type}
                    fieldValue="[ Disabled Custom Field ]"
                  />
                );
            }
            return <div>{id}</div>;
        }
      }
    },
    [customFieldsSettingsMap, netRevenueLabel]
  );

  const saveChangesHandler = useCallback(async () => {
    const api = layoutRef.current;
    if (api && fetchedSavedState) {
      const obj = api.serialize();
      loadingHandler && loadingHandler(true);
      const res = await UpdateCustomerSettingBySettingName(
        layoutDataSettingName,
        JSON.stringify(obj),
        errorHandler
      );
      loadingHandler && loadingHandler(false);
      if (!res) return false;
      setInitialState(obj);
    }
    return true;
  }, [fetchedSavedState, layoutDataSettingName, loadingHandler, errorHandler]);

  const resetLayoutHandler = useCallback(() => {
    setLastSavedState(null);
    setShowResetToDefaultConfirmDialog(false);
    setLayoutSwapControl((prev) => !prev);
  }, []);

  const layoutResizeStopHandler = useCallback(() => {
    setScreenResizedControl((prev) => !prev);
    layoutChangeHandler();
  }, [layoutChangeHandler]);

  const renderLayout = useCallback(() => {
    if (!fetchedSavedState) return <></>;
    return (
      <DashboardLayoutComponent
        cellSpacing={cellSpacing}
        columns={columns}
        cellAspectRatio={12 / 2}
        allowResizing
        resizeStop={layoutResizeStopHandler}
        change={layoutChangeHandler}
        ref={(l) => (layoutRef.current = l)}
      >
        {elements.map((e) => (
          <PanelWrapper
            key={e.id}
            id={e.id}
            col={e.col}
            row={e.row}
            sizeX={e.sizeX}
            sizeY={e.sizeY}
            minSizeX={e.minSizeX}
            minSizeY={e.minSizeY}
            maxSizeX={e.maxSizeX}
            maxSizeY={e.maxSizeY}
            resizeControl={screenResizedControl}
            resizeIconColor={theme.palette.text.disabled}
            gapX={gapX}
            gapY={gapY}
            unitWidth={unitWidth}
            unitHeight={unitHeight}
            removeHandler={removeElementHandler}
          >
            {renderElement(e.id, e.type)}
          </PanelWrapper>
        ))}
      </DashboardLayoutComponent>
    );
  }, [
    elements,
    fetchedSavedState,
    theme.palette.text.disabled,
    screenResizedControl,
    layoutResizeStopHandler,
    layoutChangeHandler,
    removeElementHandler,
    renderElement,
  ]);

  const { unsavedChangesDialog } = useUnsavedChangesDialog(
    hasChanges,
    saveChangesHandler,
    undefined,
    undefined,
    true
  );

  const addedElementTypes = useMemo(() => {
    const api = layoutRef.current;
    if (api && showAddElementDialog) {
      const t = api.serialize();
      return t.map((e) => {
        const id = e.id;
        if (id) {
          const i = id.indexOf('_');
          return id.substring(0, i);
        }
        return '';
      });
    }
    return [];
  }, [showAddElementDialog]);

  return (
    <>
      {unsavedChangesDialog}
      <ConfirmationDialog
        message="Are you sure you want to reset the current layout to its default form? All changes will be lost"
        title="Confirm Action"
        onClose={() => setShowResetToDefaultConfirmDialog(false)}
        onContinue={resetLayoutHandler}
        open={showResetToDefaultConfirmDialog}
        fullWidth
      />
      <AddPlacementDashboardElementDialog
        open={showAddElementDialog}
        activeFields={activeFields}
        alreadyAddedElements={addedElementTypes}
        closeHandler={() => setShowAddElementDialog(false)}
        addElementHandler={addElementHandler}
      />
      <div style={{ marginBottom: '10px' }}>
        <span>
          <Button
            variant="contained"
            endIcon={<ReplayIcon />}
            onClick={() => setShowResetToDefaultConfirmDialog(true)}
            sx={{ mr: '5px' }}
          >
            Reset to default
          </Button>
          <Button
            variant="contained"
            endIcon={<AddIcon />}
            onClick={() => setShowAddElementDialog(true)}
          >
            Add Element
          </Button>
        </span>
        <Button
          color="success"
          variant="contained"
          endIcon={<SaveIcon />}
          disabled={!hasChanges}
          onClick={saveChangesHandler}
          sx={{ float: 'right' }}
        >
          Save
        </Button>
      </div>
      <Box p="10px" height="100%">
        <div className="control-section">
          {layoutSwapControl && renderLayout()}
          {!layoutSwapControl && renderLayout()}
        </div>
      </Box>
    </>
  );
}
